import i18n from "../../../locales/i18n";

/**
 * The text cap-backend sends in place of a technical server message
 * (CMP_GENERIC_USER_ERROR_MESSAGE). The screen shows its own task text instead.
 */
export const CMP_GENERIC_USER_ERROR_MESSAGE =
  "We could not finish this request. Refresh the page and try again.";

export const NETWORK_ERROR_TEXT_EN =
  "We could not reach Chamber Member Pro. Check your internet connection and try again.";

/** Staff support contact (Moeen's decision, Oct 19 2026). */
export const STAFF_SUPPORT_EMAIL = "moeen@onechambersystem.com";

/**
 * A client error made on purpose whose message is ready for the user to read.
 * getUserFacingErrorText shows its message. Every other client error shows the
 * caller's fallback text.
 */
export class UserFacingError extends Error {
  readonly isUserFacingError = true;

  constructor(message: string) {
    super(message);
    this.name = "UserFacingError";
    // The build targets ES5, so instanceof needs the prototype set by hand.
    Object.setPrototypeOf(this, UserFacingError.prototype);
  }
}

const isUserFacingError = (error: unknown): error is UserFacingError =>
  error instanceof UserFacingError ||
  Boolean(
    error &&
      typeof error === "object" &&
      (error as { isUserFacingError?: unknown }).isUserFacingError === true &&
      typeof (error as { message?: unknown }).message === "string"
  );

type ServerErrorLike = { message?: unknown };
type ApolloErrorLike = {
  graphQLErrors?: unknown;
  networkError?: { result?: { errors?: unknown } } | null;
};

const isApolloErrorLike = (error: object): boolean =>
  Array.isArray((error as ApolloErrorLike).graphQLErrors) ||
  "networkError" in error;

/** A server message the user can read, or undefined for empty or generic text. */
const getReadableServerMessage = (errors: unknown): string | undefined => {
  if (!Array.isArray(errors)) return undefined;
  const message = (errors as ServerErrorLike[])
    .map((item) => (item && typeof item.message === "string" ? item.message.trim() : ""))
    .find(Boolean);
  if (!message || message === CMP_GENERIC_USER_ERROR_MESSAGE) return undefined;
  return message;
};

export const getNetworkErrorText = (): string => {
  const translated =
    typeof i18n?.t === "function" ? i18n.t("userFacingErrors.network") : "";
  return typeof translated === "string" &&
    translated &&
    translated !== "userFacingErrors.network"
    ? translated
    : NETWORK_ERROR_TEXT_EN;
};

/**
 * The one rule for error text on screen (WAVE1_BRIEF client contract).
 * - Server (GraphQL) error of any code: its message, unless it is empty or the
 *   backend generic text. Then the fallback.
 * - No server reply (network error, failed fetch, HTTP error with no GraphQL
 *   error): the network text.
 * - UserFacingError: its message.
 * - Anything else (client guard text, JavaScript errors, Stripe.js errors):
 *   the fallback. Never the raw error.message or the "GraphQL error:" wrapper.
 */
export const getUserFacingErrorText = (error: unknown, fallback: string): string => {
  if (isUserFacingError(error)) return error.message.trim() || fallback;
  if (!error || typeof error !== "object" || !isApolloErrorLike(error)) {
    return fallback;
  }

  const { graphQLErrors, networkError } = error as ApolloErrorLike;
  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    return getReadableServerMessage(graphQLErrors) || fallback;
  }
  if (networkError) {
    // A 500 from the API can carry its GraphQL errors only on the network error.
    const resultErrors = networkError.result?.errors;
    if (Array.isArray(resultErrors) && resultErrors.length > 0) {
      return getReadableServerMessage(resultErrors) || fallback;
    }
    return getNetworkErrorText();
  }
  return fallback;
};
